import {
  BLACK,
  REACTIONS,
  currentGame,
  isGameOver,
  type Command,
  type Game,
  type PlayerSlot,
  type RoomState,
} from '@chess/core'
import { useEffect, useRef, useState, type FormEvent, type JSX } from 'react'
import { formatTimeOfDay } from '../lib/format.js'
import { hapticsEnabled, hapticsSupported, setHapticsEnabled, vibrate } from '../lib/haptics.js'
import { isMuted, setMuted, subscribeToMute } from '../sound/engine.js'

type Tab = 'moves' | 'chat'

export interface SidePanelProps {
  readonly room: RoomState
  readonly game: Game
  readonly ply: number
  readonly onSeek: (ply: number) => void
  readonly send: (command: Command) => void
  readonly mySlot: PlayerSlot | null
  readonly canAct: boolean
  readonly onFlip: () => void
  readonly onCopyPgn: () => void
  readonly unreadChat: number
  readonly onReadChat: () => void
}

export const SidePanel = ({
  room,
  game,
  ply,
  onSeek,
  send,
  mySlot,
  canAct,
  onFlip,
  onCopyPgn,
  unreadChat,
  onReadChat,
}: SidePanelProps): JSX.Element => {
  const [tab, setTab] = useState<Tab>('moves')

  useEffect(() => {
    if (tab === 'chat') onReadChat()
  }, [tab, onReadChat, room.chat.length])

  return (
    <section className="flex min-h-0 flex-1 flex-col rounded-panel bg-panel lg:h-[calc(100vh-2rem)]">
      <ScoreHeader room={room} mySlot={mySlot} />

      <div className="flex border-b border-panel-line" role="tablist">
        {(['moves', 'chat'] as const).map((name) => (
          <button
            key={name}
            type="button"
            role="tab"
            aria-selected={tab === name}
            onClick={() => setTab(name)}
            className={`relative flex-1 px-3 py-2 text-sm font-semibold capitalize transition-colors ${
              tab === name ? 'bg-panel-raised text-ink' : 'text-ink-dim hover:text-ink'
            }`}
          >
            {name}
            {name === 'chat' && unreadChat > 0 && tab !== 'chat' && (
              <span className="ml-1.5 rounded-full bg-accent px-1.5 text-xs text-black">
                {unreadChat}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'moves' ? (
        <>
          <MoveList game={game} ply={ply} onSeek={onSeek} />
          <MoveNavigation ply={ply} livePly={game.history.length} onSeek={onSeek} />
        </>
      ) : (
        <ChatPanel room={room} send={send} mySlot={mySlot} />
      )}

      <Controls
        room={room}
        game={game}
        send={send}
        mySlot={mySlot}
        canAct={canAct}
        onFlip={onFlip}
        onCopyPgn={onCopyPgn}
      />
    </section>
  )
}

const ScoreHeader = ({
  room,
  mySlot,
}: {
  readonly room: RoomState
  readonly mySlot: PlayerSlot | null
}): JSX.Element => {
  const me = mySlot ?? 0
  const them: PlayerSlot = me === 0 ? 1 : 0
  const played = room.games.filter((slot) => isGameOver(slot.game.status)).length

  return (
    <header className="no-select flex items-baseline justify-between border-b border-panel-line px-3 py-2.5">
      <span className="text-sm font-semibold">
        {played === 0 ? 'First game' : `Game ${room.games.length}`}
      </span>
      <span className="font-mono text-sm tabular-nums text-ink-dim">
        <span className="text-ink">{room.score.wins[me]}</span>
        {' – '}
        <span className="text-ink">{room.score.wins[them]}</span>
        {room.score.draws > 0 && <span className="ml-2">{room.score.draws} drawn</span>}
      </span>
    </header>
  )
}

/** Distance from the bottom that still counts as "following along". */
const FOLLOW_THRESHOLD_PX = 48

const MoveList = ({
  game,
  ply,
  onSeek,
}: {
  readonly game: Game
  readonly ply: number
  readonly onSeek: (ply: number) => void
}): JSX.Element => {
  const scroller = useRef<HTMLDivElement>(null)

  /**
   * Follow the game only if you were already at the bottom. Yanking the list
   * down while someone is scrolled up reading the opening is the single most
   * irritating thing a move list can do.
   */
  useEffect(() => {
    const node = scroller.current
    if (node === null) return
    const distance = node.scrollHeight - node.scrollTop - node.clientHeight
    if (distance > FOLLOW_THRESHOLD_PX) return
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
  }, [game.history.length])

  // A game that starts with Black to move needs an empty White cell on row one,
  // which also means one extra row is needed to hold the same number of moves.
  const firstIsBlack = game.initial.turn === BLACK
  const rows = Array.from(
    { length: Math.ceil((game.history.length + (firstIsBlack ? 1 : 0)) / 2) },
    (_, index) => index,
  )

  return (
    <div ref={scroller} className="min-h-24 flex-1 overflow-y-auto lg:min-h-0">
      {game.history.length === 0 ? (
        <p className="px-3 py-4 text-sm text-ink-dim">No moves yet.</p>
      ) : (
        <ol className="text-sm">
          {rows.map((row) => {
            const whitePly = row * 2 + (firstIsBlack ? 0 : 1)
            const blackPly = whitePly + 1
            return (
              <li key={row} className="flex items-stretch odd:bg-white/[0.02]">
                <span className="w-10 shrink-0 py-1 pr-1 text-right font-mono text-xs text-ink-dim">
                  {game.initial.fullmoves + row}.
                </span>
                <MoveCell game={game} target={whitePly} ply={ply} onSeek={onSeek} skip={firstIsBlack && row === 0} />
                <MoveCell game={game} target={blackPly} ply={ply} onSeek={onSeek} skip={false} />
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

const MoveCell = ({
  game,
  target,
  ply,
  onSeek,
  skip,
}: {
  readonly game: Game
  readonly target: number
  readonly ply: number
  readonly onSeek: (ply: number) => void
  readonly skip: boolean
}): JSX.Element => {
  const entry = game.history[target - 1]
  if (skip) return <span className="flex-1 py-1" />
  if (entry === undefined) return <span className="flex-1 py-1" />

  return (
    <button
      type="button"
      onClick={() => onSeek(target)}
      className={`flex-1 px-2 py-1 text-left font-medium ${
        ply === target ? 'rounded bg-panel-raised text-ink' : 'text-ink-dim hover:text-ink'
      }`}
    >
      {entry.san}
    </button>
  )
}

/**
 * Step through the game.
 *
 * The arrow keys did this already, which is no use at all on a phone — where
 * this app is mostly played. Disabled at the ends rather than hidden, so the
 * row never changes height and the buttons stay where your thumb learned they
 * were.
 */
const MoveNavigation = ({
  ply,
  livePly,
  onSeek,
}: {
  readonly ply: number
  readonly livePly: number
  readonly onSeek: (ply: number) => void
}): JSX.Element => {
  const steps = [
    { label: '⏮', to: 0, enabled: ply > 0, title: 'First move' },
    { label: '◀', to: ply - 1, enabled: ply > 0, title: 'Previous move' },
    { label: '▶', to: ply + 1, enabled: ply < livePly, title: 'Next move' },
    { label: '⏭', to: livePly, enabled: ply < livePly, title: 'Latest move' },
  ]

  return (
    <div className="flex border-t border-panel-line">
      {steps.map((step) => (
        <button
          key={step.title}
          type="button"
          title={step.title}
          aria-label={step.title}
          disabled={!step.enabled}
          onClick={() => {
            vibrate('select')
            onSeek(step.to)
          }}
          className="flex-1 py-3 text-sm text-ink transition-colors hover:bg-panel-raised disabled:opacity-25"
        >
          {step.label}
        </button>
      ))}
    </div>
  )
}

const ChatPanel = ({
  room,
  send,
  mySlot,
}: {
  readonly room: RoomState
  readonly send: (command: Command) => void
  readonly mySlot: PlayerSlot | null
}): JSX.Element => {
  const [draft, setDraft] = useState('')
  /** Messages we have sent but not yet seen echoed back by the server. */
  const [pending, setPending] = useState<readonly string[]>([])
  const scroller = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight })
  }, [room.chat.length, pending.length])

  // Anything the server has now echoed can stop being shown as pending.
  useEffect(() => {
    if (pending.length === 0) return
    const mine = new Set(
      room.chat.filter((message) => message.from === mySlot).map((message) => message.text),
    )
    const still = pending.filter((text) => !mine.has(text))
    if (still.length !== pending.length) setPending(still)
  }, [room.chat, mySlot, pending])

  const submit = (event: FormEvent): void => {
    event.preventDefault()
    const text = draft.trim()
    if (text === '') return
    send({ type: 'chat', text })
    // Echo locally straight away: on a slow link the round trip is long enough
    // that an unacknowledged message reads as dropped.
    setPending((current) => [...current, text.replace(/\s+/g, ' ')])
    setDraft('')
  }

  return (
    <div className="flex min-h-32 flex-1 flex-col lg:min-h-0">
      <div ref={scroller} className="flex-1 space-y-2 overflow-y-auto px-3 py-2">
        {room.chat.length === 0 && pending.length === 0 && (
          <p className="text-sm text-ink-dim">Say hello.</p>
        )}
        {room.chat.map((message, index) => (
          <p key={index} className="text-sm leading-snug">
            <span className="font-semibold text-accent-strong">{message.name}</span>
            <span className="ml-1.5 text-[10px] text-ink-dim">{formatTimeOfDay(message.at)}</span>
            <br />
            <span className="break-words text-ink">{message.text}</span>
          </p>
        ))}
        {pending.map((text, index) => (
          <p key={`pending-${index}`} className="text-sm leading-snug opacity-45">
            <span className="font-semibold text-accent-strong">You</span>
            <span className="ml-1.5 text-[10px] text-ink-dim">sending…</span>
            <br />
            <span className="break-words text-ink">{text}</span>
          </p>
        ))}
      </div>
      <form onSubmit={submit} className="keyboard-inset border-t border-panel-line p-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          maxLength={300}
          placeholder="Message"
          aria-label="Chat message"
          className="w-full rounded bg-panel-raised px-3 py-2 text-base text-ink outline-none placeholder:text-ink-dim focus:ring-1 focus:ring-accent"
        />
      </form>
    </div>
  )
}

/**
 * Two-step rather than a `confirm()` dialog: resigning is irreversible, but a
 * modal that blocks the whole tab is a heavy way to ask, and it steals focus
 * mid-game.
 */
const ResignButton = ({
  disabled,
  onResign,
}: {
  readonly disabled: boolean
  readonly onResign: () => void
}): JSX.Element => {
  const [armed, setArmed] = useState(false)

  useEffect(() => {
    if (!armed) return
    const id = window.setTimeout(() => setArmed(false), 3_000)
    return () => window.clearTimeout(id)
  }, [armed])

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        if (armed) onResign()
        else setArmed(true)
      }}
      className={`flex-1 rounded px-3 py-2 text-sm font-semibold disabled:opacity-40 ${
        armed ? 'bg-danger text-white' : 'bg-panel-raised hover:bg-panel-line'
      }`}
    >
      {armed ? 'Sure?' : 'Resign'}
    </button>
  )
}

/** Sound and vibration, both persisted, both previously unreachable from the UI. */
const FeedbackToggles = (): JSX.Element => {
  const [muted, setMutedState] = useState(isMuted)
  const [haptics, setHaptics] = useState(hapticsEnabled)

  useEffect(() => subscribeToMute(setMutedState), [])

  return (
    <>
      <button
        type="button"
        title={muted ? 'Unmute' : 'Mute'}
        aria-label={muted ? 'Unmute sounds' : 'Mute sounds'}
        aria-pressed={muted}
        onClick={() => setMuted(!muted)}
        className="rounded bg-panel-raised px-3 py-2 text-sm font-semibold hover:bg-panel-line"
      >
        {muted ? '🔇' : '🔊'}
      </button>
      {hapticsSupported() && (
        <button
          type="button"
          title={haptics ? 'Turn off vibration' : 'Turn on vibration'}
          aria-label={haptics ? 'Turn off vibration' : 'Turn on vibration'}
          aria-pressed={haptics}
          onClick={() => {
            setHapticsEnabled(!haptics)
            setHaptics(!haptics)
            if (!haptics) vibrate('move')
          }}
          className={`rounded px-3 py-2 text-sm font-semibold ${
            haptics ? 'bg-panel-raised hover:bg-panel-line' : 'bg-panel-raised opacity-45'
          }`}
        >
          📳
        </button>
      )}
    </>
  )
}

const Controls = ({
  room,
  game,
  send,
  mySlot,
  canAct,
  onFlip,
  onCopyPgn,
}: {
  readonly room: RoomState
  readonly game: Game
  readonly send: (command: Command) => void
  readonly mySlot: PlayerSlot | null
  readonly canAct: boolean
  readonly onFlip: () => void
  readonly onCopyPgn: () => void
}): JSX.Element => {
  const over = isGameOver(game.status)
  const offer = currentGame(room).drawOfferedBy
  const isBotGame = room.opponent.kind === 'ai'
  const awaitingOpponent = room.rematchOfferedBy !== null && room.rematchOfferedBy === mySlot

  return (
    <footer className="space-y-2 border-t border-panel-line p-2 safe-bottom">
      <div className="flex gap-1">
        {REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            disabled={!canAct}
            onClick={() => send({ type: 'react', emoji })}
            className="flex-1 rounded py-2 text-lg transition-colors hover:bg-panel-raised disabled:opacity-40"
            aria-label={`React with ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onFlip}
          className="rounded bg-panel-raised px-3 py-2 text-sm font-semibold hover:bg-panel-line"
          title="Flip board"
          aria-label="Flip board"
        >
          ⇅
        </button>
        <button
          type="button"
          onClick={onCopyPgn}
          className="rounded bg-panel-raised px-3 py-2 text-sm font-semibold hover:bg-panel-line"
          title="Copy PGN"
          aria-label="Copy PGN"
        >
          PGN
        </button>
        <FeedbackToggles />

        {over ? (
          <button
            type="button"
            disabled={!canAct || awaitingOpponent}
            onClick={() => send({ type: 'offerRematch' })}
            className="min-w-28 flex-1 rounded bg-accent px-3 py-2 text-sm font-bold text-black hover:bg-accent-strong disabled:opacity-40"
          >
            {awaitingOpponent ? 'Waiting…' : room.rematchOfferedBy === null ? 'Rematch' : 'Accept rematch'}
          </button>
        ) : offer !== null ? (
          <>
            <button
              type="button"
              disabled={!canAct}
              onClick={() => send({ type: 'respondDraw', accept: true })}
              className="min-w-24 flex-1 rounded bg-accent px-2 py-2 text-sm font-bold text-black hover:bg-accent-strong disabled:opacity-40"
            >
              Accept draw
            </button>
            <button
              type="button"
              disabled={!canAct}
              onClick={() => send({ type: 'respondDraw', accept: false })}
              className="rounded bg-panel-raised px-3 py-2 text-sm font-semibold hover:bg-panel-line disabled:opacity-40"
            >
              Decline
            </button>
          </>
        ) : (
          <>
            {/* A bot never accepts a draw, so the button would only ever be a
                dead end. */}
            {!isBotGame && (
              <button
                type="button"
                disabled={!canAct}
                onClick={() => send({ type: 'offerDraw' })}
                className="min-w-20 flex-1 rounded bg-panel-raised px-3 py-2 text-sm font-semibold hover:bg-panel-line disabled:opacity-40"
              >
                ½ Draw
              </button>
            )}
            <ResignButton disabled={!canAct} onResign={() => send({ type: 'resign' })} />
          </>
        )}
      </div>
    </footer>
  )
}
